import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, Loader2, AlertTriangle } from "lucide-react";
import { logger } from "@/lib/logger";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (n) => {
  if (n === undefined || n === null || isNaN(n)) return "0.00";
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(n));
};

const monthLabel = (m) => {
  if (!m || !m.includes("-")) return m;
  const [y, mm] = m.split("-");
  const names = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun",
                 "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${names[parseInt(mm, 10)]}-${y.slice(-2)}`;
};

/**
 * Modal that fetches and renders the party / ledger-wise drill-down for
 * a Balance Sheet section. Hits `/api/bs/drilldown`.
 *
 * Props:
 *   open          : boolean
 *   onClose       : () => void
 *   groupName     : str — BS section as it appears in the canonical payload
 *   companyId, branchId, fromDate, toDate
 *   sideColor     : 'asset' | 'liability'  (header tint)
 */
export default function BSDrillDialog({
  open, onClose, groupName,
  companyId, branchId, fromDate, toDate,
  sideColor = "asset",
}) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open || !groupName) return;
    let alive = true;
    setLoading(true); setErr(null); setData(null); setQuery("");
    const params = { group_name: groupName, company_id: companyId };
    if (branchId) params.branch_id = branchId;
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    axios.get(`${API}/bs/drilldown`, { params })
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => {
        logger.error("BS drill failed:", e);
        if (alive) setErr(e?.response?.data?.detail || "Failed to load drill-down");
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, groupName, companyId, branchId, fromDate, toDate]);

  if (!open) return null;

  const headerTint = sideColor === "liability"
    ? "from-amber-50 to-amber-100 text-amber-900"
    : "from-blue-50 to-blue-100 text-blue-900";
  const accent = sideColor === "liability" ? "text-amber-700" : "text-blue-700";

  const parties = data?.parties || [];
  const months = data?.months || [];
  const filtered = query
    ? parties.filter((p) => p.party.toLowerCase().includes(query.toLowerCase()))
    : parties;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      data-testid="bs-drill-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="w-full max-w-5xl max-h-[88vh] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden border border-slate-200"
        data-testid="bs-drill-dialog"
      >
        <div className={`px-6 py-4 bg-gradient-to-br ${headerTint} flex items-start justify-between gap-4 border-b border-slate-200`}>
          <div className="min-w-0">
            <p className="text-[10.5px] font-semibold uppercase tracking-[0.18em] opacity-70">
              Party-wise drill-down
            </p>
            <h2 className="text-xl font-bold tracking-tight truncate" data-testid="bs-drill-title">
              {groupName}
            </h2>
            <p className={`text-sm mt-1 font-mono tabular-nums ${accent}`} data-testid="bs-drill-total">
              ₹ {fmt(data?.total)} <span className="text-[11px] text-slate-500 ml-2">
                · {parties.length} parties · {months.length} month{months.length === 1 ? "" : "s"}
              </span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-white/60 transition"
            aria-label="Close"
            data-testid="bs-drill-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-3 border-b border-slate-100 bg-slate-50/40 flex items-center gap-3">
          <input
            type="text"
            placeholder={`Search ${parties.length} parties…`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1 max-w-xs px-3 py-1.5 text-[12.5px] border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-300"
            data-testid="bs-drill-search"
          />
          {query && (
            <span className="text-[11px] text-slate-500">
              {filtered.length} of {parties.length}
            </span>
          )}
        </div>

        <div className="flex-1 overflow-auto">
          {loading && (
            <div className="flex items-center justify-center py-16 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading drill-down…
            </div>
          )}
          {err && (
            <div className="flex items-center justify-center py-16 text-amber-700 gap-2">
              <AlertTriangle className="w-5 h-5" /> {err}
            </div>
          )}
          {!loading && !err && data && (
            <table className="w-full text-[12.5px]" data-testid="bs-drill-table">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr className="border-b border-slate-200">
                  <th className="text-left font-semibold text-slate-700 px-6 py-2.5">Party / Ledger</th>
                  {months.map((m) => (
                    <th key={m} className="text-right font-semibold text-slate-700 px-3 py-2.5 w-28">
                      {monthLabel(m)}
                    </th>
                  ))}
                  <th className={`text-right font-semibold px-6 py-2.5 w-32 ${accent}`}>
                    Closing
                  </th>
                </tr>
              </thead>
              <tbody className="font-mono tabular-nums">
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={months.length + 2} className="text-center text-slate-400 py-12 font-sans">
                      {query ? "No parties match your search." : "No party-level detail found for this section."}
                    </td>
                  </tr>
                )}
                {filtered.map((p) => (
                  <tr
                    key={p.party}
                    className="border-b border-slate-100 hover:bg-slate-50/60"
                    data-testid={`bs-drill-row-${p.party?.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    <td className="px-6 py-1.5 font-sans text-slate-700 truncate max-w-md" title={p.party}>
                      {p.party === "Unallocated / Opening BF" ? (
                        <span className="italic text-amber-700">{p.party}</span>
                      ) : p.party}
                    </td>
                    {months.map((m) => (
                      <td key={m} className="text-right px-3 py-1.5 text-slate-600">
                        {fmt(p.monthly?.[m])}
                      </td>
                    ))}
                    <td className={`text-right px-6 py-1.5 font-semibold ${accent}`}>
                      {fmt(p.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
              {parties.length > 0 && (
                <tfoot>
                  <tr className="bg-slate-100 font-bold border-t-2 border-slate-300">
                    <td className="px-6 py-2.5 font-sans">Total</td>
                    {months.map((m) => (
                      <td key={m} className="text-right px-3 py-2.5">
                        {fmt(data?.monthly_totals?.[m])}
                      </td>
                    ))}
                    <td className={`text-right px-6 py-2.5 ${accent}`}>
                      {fmt(data?.total)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
